// Types.
import type { direction } from '../Types/Direction';
import type event from "../Types/Event";
import type source from "../Types/Source";
import type { progressCallback } from '../Types/Callbacks';

// Constants.
import { directions } from '../Types/Direction';

// Interfaces.
import { ProgressOptions } from '../Interfaces/Options';

// Classes.
import Direction from './Direction';
import Handler from './Handler';
import Listener from "./Listener";

/**
 * Scrolly.Progress
 *
 * @author: FelixKiesewetter <info@felixkiesewetter.de>
 * @lastModified: 2026-06-16 Fixed above-the-fold progress.
 * @package fk_js/scroll
 * @version: 1.1.0
 */
class Progress extends Handler<ProgressOptions>
{

    private active                  :boolean = false;
    private direction               :direction = directions.DOWN;
    private distance                :number = 0;
    private intersectionObserver    :IntersectionObserver;
    private intersectionElement     :HTMLElement|undefined;
    private nodeHeight              :number = 0;
    private nodeOffsetTop           :number = 0;
    private progress                :number = 0;
    private ratio                   :number = 0;

    /**
     * Constructor.
     */
    constructor(source :source, options :ProgressOptions)
    {
        // TODO start: maybe all handlers should have setNode()
        super(options);
        this.setNode(source);
        // TODO end.

        // Observe scroll direction.
        Direction.observe(window, {
            dataset: this.options.dataset,
            debug: this.options.dataset,
            onChange: (args) :void => {
                this.direction = args.direction;
            }
        });

        // TODO: Catch STICKY (see Scene and Hauser)

        // Init intersection observer.
        this.intersectionObserver = new IntersectionObserver((entries :IntersectionObserverEntry[]) :void => {
            entries.forEach((entry :IntersectionObserverEntry) :void => {
                if (entry.isIntersecting) {
                    this.setActive(true);
                }
                else {
                    this.setActive(false);
                    if (directions.DOWN === this.direction) {
                        this.setDistance(this.nodeHeight).setProgress(0);
                    }
                    else {
                        this.setDistance(0).setProgress(100);
                    }
                }
            });
        });

        // Observe the intersection element.
        this.intersectionObserver.observe(this.createIntersectionElement());
    }

    // ################
    // Abstract methods
    // ################

    /**
     * Define default settings.
     */
    public defaults() :ProgressOptions
    {
        return {
            cssVars: true,
            dataset: false,
            debug: false,
            onChange: null,
            thresholdTop: 0,
            thresholdBottom: 0,
        };
    }

    /**
     * This callback listens to the scroll event.
     */
    public events() :event[]
    {
        return [ 'init', 'scroll', 'resize' ];
    }

    /**
     * Check if an animation frame should be requested or not.
     */
    public requestAnimationFrames(event :Event, handler :Listener) :Function[]
    {
        let animationFrames :Function[] = [];

        // Get the offset from the element from the page's top, also respecting sticky navigations.
        // TODO: This works fine when scrolling down, but scrolling UP with a sticky nav will keep the offset at the lower value :-(
        const nodeOffsetTop :number = this.getNode().getBoundingClientRect().top + window.scrollY;

        // On resize: re-calculate some properties.
        if (
            'resize' === event.type ||
            'init' === event.type ||
            nodeOffsetTop !== this.nodeOffsetTop // when using sticky elements, the nodeOffsetTop can change even when no resize happened.
        ) {
            animationFrames.push(() :void => {

                // Update node height and offset.
                this.nodeHeight = this.getNode().getBoundingClientRect().height;
                this.nodeOffsetTop = nodeOffsetTop;

                // Update progress-observing element.
                if (this.intersectionElement) {
                    let intersectionElementTop :number = this.nodeOffsetTop + ((this.options.thresholdTop || 0) * window.innerHeight);
                    let intersectionElementHeight :number = this.nodeHeight - ((this.options.thresholdTop || 0) * window.innerHeight) - ((this.options.thresholdBottom || 0) * window.innerHeight);
                    if (intersectionElementTop < window.innerHeight) {
                        // The node is above-the-fold, we don't want to start at 50% ...
                        intersectionElementTop = this.nodeOffsetTop + this.nodeHeight;
                        intersectionElementHeight = Math.max(intersectionElementHeight - intersectionElementTop, 1);
                    }
                    this.intersectionElement.style.top = `${intersectionElementTop}px`;
                    this.intersectionElement.style.height = `${intersectionElementHeight}px`;
                }
            });
        }

        // On scroll while active: calculate progress.
        else if ('scroll' === event.type && this.active) {
            let distance :number = 0;
            let progress :number = 0;
            if (this.intersectionElement) {
                distance = window.innerHeight - this.intersectionElement.getBoundingClientRect().top;
                progress = Math.round((distance / (this.intersectionElement.scrollHeight + window.innerHeight)) * 100);
            }
            else {
                // TODO: Do we really need a fallback?
            }
            if (progress !== this.progress) {
                animationFrames.push(() :void => {
                    this.setProgress(progress).setDistance(distance);
                    this.options.onChange && this.options.onChange({
                        progress: this.progress,
                        ratio: this.ratio,
                        distance: this.distance,
                        direction: this.direction,
                    });
                });
            }
        }

        return animationFrames;
    }

    // ###############
    // Private methods
    // ###############

    /**
     * Create an element which will be observed for scroll progress.
     */
    private createIntersectionElement() :HTMLElement
    {
        this.intersectionElement = document.createElement('aside');
        this.intersectionElement.style.position = 'absolute';
        this.intersectionElement.style.zIndex = '1000';
        this.intersectionElement.style.left = '0';
        if (this.options.debug) {
            this.intersectionElement.style.backgroundColor = '#0F0C';
            this.intersectionElement.style.width = '10px';
        }
        else {
            this.intersectionElement.style.backgroundColor = 'transparent';
            this.intersectionElement.style.width = '1px';
        }
        document.body.appendChild(this.intersectionElement);
        return this.intersectionElement;
    }

    // ##############
    // Public methods
    // ##############

    // ...

    // #######
    // Getters
    // #######

    /**
     * Getter for the current progress (%).
     */
    public get() :number
    {
        return this.progress;
    }

    /**
     * Getter for the currently scrolled distance (px).
     */
    public getDistance() :number
    {
        return this.distance;
    }

    /**
     * Getter for the total length which can be scrolled by.
     */
    public getLength() :number
    {
        return this.nodeHeight;
    }

    /**
     * Getter for the current progress (%).
     */
    public getProgress() :number
    {
        return this.progress;
    }

    /**
     * Getter for the current ratio (0-1).
     */
    public getRatio() :number
    {
        return this.ratio;
    }

    /**
     * Getter for the current active state.
     */
    public isActive() :boolean
    {
        return this.active;
    }

    // #######
    // Setters
    // #######

    /**
     * Setter for the active state.
     */
    public setActive(active :boolean) :Progress
    {
        if (active === this.active) {
            return this;
        }
        this.active = active;
        return this;
    }

    /**
     * Setter for the distance, which is the amount of scrolled pixels within the section.
     */
    private setDistance(distance :number) :Progress
    {
        this.distance = distance;
        if (this.options.dataset) {
            this.getNode().dataset.scrollyDistance = `${distance}`;
        }
        if (this.options.cssVars) {
            this.getNode().style.setProperty('--scrolly-distance', `${distance}px`);
        }
        return this;
    }

    /**
     * Setter for the current progress.
     */
    private setProgress(progress :number) :Progress
    {
        this.progress = progress;
        this.setRatio(progress / 100);
        if (this.options.dataset) {
            this.getNode().dataset.scrollyProgress = `${progress}`;
        }
        if (this.options.cssVars) {
            this.getNode().style.setProperty('--scrolly-progress', `${progress}%`);
        }
        return this;
    }

    /**
     * Setter for the ratio.
     */
    private setRatio(ratio :number) :Progress
    {
        this.ratio = ratio;
        if (this.options.dataset) {
            this.getNode().dataset.scrollyRatio = `${ratio}`;
        }
        if (this.options.cssVars) {
            this.getNode().style.setProperty('--scrolly-ratio', `${ratio}`);
        }
        return this;
    }

    // ##############
    // Static methods
    // ##############

    static observe(source :source = window, options :ProgressOptions) :Progress
    {
        return new Progress(source, options);
    }

}

export default Progress;